'use client';
import React from 'react';

interface ExplanationPanelProps {
  isCorrect: boolean;
  explanation: string;
  ruleReference: string;
  learningTakeaway: string;
  correctAnswerText: string;
}

export default function ExplanationPanel({
  isCorrect,
  explanation,
  ruleReference,
  learningTakeaway,
  correctAnswerText,
}: ExplanationPanelProps) {
  return (
    <div
      className="explanation-slide rounded-2xl overflow-hidden"
      style={{
        background: isCorrect ? '#ECFDF5' : '#FEF2F2',
        border: `1px solid ${isCorrect ? 'rgba(5,150,105,0.25)' : 'rgba(220,38,38,0.25)'}`,
      }}
      role="region"
      aria-label="Answer explanation"
      aria-live="polite"
    >
      {/* Header */}
      <div
        className="flex items-center gap-3 px-5 py-4"
        style={{
          background: isCorrect ? 'rgba(5,150,105,0.1)' : 'rgba(220,38,38,0.08)',
          borderBottom: `1px solid ${isCorrect ? 'rgba(5,150,105,0.2)' : 'rgba(220,38,38,0.18)'}`,
        }}
      >
        {isCorrect ? (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <circle cx="10" cy="10" r="9" fill="rgba(5,150,105,0.15)" stroke="#059669" strokeWidth="1.5" />
            <path d="M6 10l3 3 5-5" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <circle cx="10" cy="10" r="9" fill="rgba(220,38,38,0.1)" stroke="#DC2626" strokeWidth="1.5" />
            <path d="M7 7l6 6M13 7l-6 6" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" />
          </svg>
        )}
        <div className="flex-1">
          <span className="font-semibold text-sm" style={{ color: isCorrect ? '#059669' : '#DC2626' }}>
            {isCorrect ? 'Correct!' : 'Incorrect'}
          </span>
          {!isCorrect && (
            <p className="text-xs mt-0.5" style={{ color: '#64748B' }}>
              The correct answer was:{' '}
              <span className="font-medium" style={{ color: '#059669' }}>{correctAnswerText}</span>
            </p>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-5 flex flex-col gap-5">
        {/* Explanation */}
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#94A3B8' }}>
            Explanation
          </h3>
          <p className="text-sm leading-relaxed" style={{ color: '#334155' }}>
            {explanation}
          </p>
        </div>

        {/* Rule reference */}
        <div
          className="rounded-xl p-4"
          style={{ background: 'rgba(30,64,175,0.06)', border: '1px solid rgba(30,64,175,0.12)' }}
        >
          <div className="flex items-start gap-2.5">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="shrink-0 mt-0.5" aria-hidden="true">
              <path d="M4 2h8a1 1 0 011 1v10a1 1 0 01-1 1H4a1 1 0 01-1-1V3a1 1 0 011-1z" stroke="#1E40AF" strokeWidth="1.2" />
              <line x1="5.5" y1="6" x2="10.5" y2="6" stroke="#1E40AF" strokeWidth="1.2" strokeLinecap="round" />
              <line x1="5.5" y1="8.5" x2="10.5" y2="8.5" stroke="#1E40AF" strokeWidth="1.2" strokeLinecap="round" />
              <line x1="5.5" y1="11" x2="8.5" y2="11" stroke="#1E40AF" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
            <div>
              <span className="text-xs font-semibold block mb-1" style={{ color: '#1E40AF' }}>Official Rule Reference</span>
              <p className="text-xs leading-relaxed" style={{ color: '#475569' }}>{ruleReference}</p>
            </div>
          </div>
        </div>

        {/* Learning takeaway */}
        <div
          className="rounded-xl p-4"
          style={{ background: 'rgba(217,119,6,0.07)', border: '1px solid rgba(217,119,6,0.15)' }}
        >
          <div className="flex items-start gap-2.5">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="shrink-0 mt-0.5" aria-hidden="true">
              <path d="M8 2a4 4 0 00-4 4c0 1.5.8 2.8 2 3.5V11h4V9.5c1.2-.7 2-2 2-3.5a4 4 0 00-4-4z" stroke="#D97706" strokeWidth="1.2" />
              <path d="M6 13h4M6.5 15h3" stroke="#D97706" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
            <div>
              <span className="text-xs font-semibold block mb-1" style={{ color: '#D97706' }}>Learning Takeaway</span>
              <p className="text-xs leading-relaxed" style={{ color: '#475569' }}>{learningTakeaway}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}